import * as S from "@/containers/Project/components/styled";
import {
  getAvailableMilestones,
  getFlattenedMilestonesWithSchedule,
} from "@/containers/Project/helper";
import { useMobileLayout } from "@/hooks/useAppMedia";
import { useProjectContext } from "@/providers";
import {
  useAddProjectMilestonesMutation,
  useGetProjectTemplateQuery,
} from "@/services";
import * as GlobalS from "@/sharedStyled";
import { Select, theme } from "antd";
import { bool, func, string } from "prop-types";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { toast } from "react-toastify";

const { Option } = Select;
const { useToken } = theme;

const AddMilestoneFromListModal = ({ isOpen, onCancel, parentId }) => {
  const intl = useIntl();
  const isMobileLayout = useMobileLayout();

  const { token } = useToken();

  const { currentSchedule, project, setProject } = useProjectContext();

  // LOCAL STATE
  const [availableMilestonesFromTemplate, setAvailableMilestonesFromTemplate] =
    useState([]);
  const [availableMilestonesFromProject, setAvailableMilestonesFromProject] =
    useState([]);
  const [selectedMilestonesFromTemplate, setSelectedMilestonesFromTemplate] =
    useState([]);
  const [selectedMilestonesFromProject, setSelectedMilestonesFromProject] =
    useState([]);
  const [targetSchedule, setTargetSchedule] = useState(null);
  const [availableSchedules, setAvailableSchedules] = useState([]);

  const [addProjectMilestones, { isLoading: addingProjectMilestones }] =
    useAddProjectMilestonesMutation();
  const { data: projectTemplate } = useGetProjectTemplateQuery(
    project.projectTemplateId,
    {
      skip: !project?.projectTemplateId,
    }
  );

  useEffect(() => {
    if (project.schedules?.length > 0) {
      setAvailableSchedules(
        project.schedules.filter((schedule) => !schedule.isDeleted)
      );
    }
  }, [project]);

  useEffect(() => {
    if (project && projectTemplate) {
      const flattenedMilestones = getFlattenedMilestonesWithSchedule(
        project.schedules
      );

      const parameters = {
        project,
        milestoneTemplates: projectTemplate.milestones,
        parentId,
        flattenedMilestones,
      };

      setAvailableMilestonesFromProject(
        getAvailableMilestones({ ...parameters, isTemplate: false })
      );
      setAvailableMilestonesFromTemplate(
        getAvailableMilestones({ ...parameters, isTemplate: true })
      );
    }

    if (!parentId && availableSchedules.length > 0) {
      setTargetSchedule(currentSchedule || availableSchedules[0].id);
    }
  }, [projectTemplate, project, parentId, availableSchedules, currentSchedule]);

  const handleSave = () => {
    addProjectMilestones({
      projectId: project.id,
      parentId,
      targetSchedule,
      fromTemplate: selectedMilestonesFromTemplate.map(({ id }) => id),
      fromProject: selectedMilestonesFromProject.map(({ id }) => id),
    })
      .unwrap()
      .then((project) => {
        setProject(project);
        toast.success(intl.formatMessage({ id: "addMilestonesModal.saved" }));
        onCancel();
      })
      .catch(() => {
        toast.error(intl.formatMessage({ id: "addMilestonesModal.error" }));
      });
  };

  const toggleSelected = (milestone, selectedList, setSelectedList) => {
    if (isCurrentlySelected(milestone.id, selectedList)) {
      setSelectedList(
        selectedList.filter((selected) => selected.id !== milestone.id)
      );
    } else {
      setSelectedList([...selectedList, milestone]);
    }
  };

  const isCurrentlySelected = (milestoneId, selectionList) =>
    selectionList.some((selected) => selected.id === milestoneId);

  const handleScheduleChange = (scheduleId) => {
    setTargetSchedule(scheduleId);
  };

  return (
    <GlobalS.ResponsiveModal
      $mobile={isMobileLayout}
      maskClosable={false}
      title={intl.formatMessage({
        id: "addMilestonesModal.title",
      })}
      onOk={handleSave}
      onCancel={onCancel}
      okButtonProps={{
        loading: addingProjectMilestones,
        disabled:
          selectedMilestonesFromProject.length +
            selectedMilestonesFromTemplate.length ===
          0,
      }}
      open={isOpen}
      okText={`Add ${
        selectedMilestonesFromProject.length +
        selectedMilestonesFromTemplate.length
      } Milestones To Project?`}
    >
      {projectTemplate && availableMilestonesFromTemplate?.length > 0 && (
        <>
          {!parentId && (
            <h3>
              {intl.formatMessage({
                id: "addMilestonesModal.targetSchedule",
              })}
              <S.StyledSelect
                disabled={!(availableSchedules?.length > 1)}
                data-test-id={`schedule-select-${project.id}`}
                value={targetSchedule}
                onChange={handleScheduleChange}
              >
                {availableSchedules.map((schedule, index) => (
                  <Option key={index} value={schedule.id}>
                    {schedule.name || index}
                  </Option>
                ))}
              </S.StyledSelect>
            </h3>
          )}
          <h3>
            {intl.formatMessage({
              id: "addMilestonesModal.fromProjectTemplate",
            })}
          </h3>
          {availableMilestonesFromTemplate.map((milestone, index) => (
            <S.SelectableMilestone
              key={`available-milestone-${index}`}
              data-test-id={`select-${milestone.id}`}
              onClick={() =>
                toggleSelected(
                  milestone,
                  selectedMilestonesFromTemplate,
                  setSelectedMilestonesFromTemplate
                )
              }
              $selected={isCurrentlySelected(
                milestone.id,
                selectedMilestonesFromTemplate
              )}
              $color={token.colorPrimaryBg}
            >
              <span>
                {milestone.abbreviation} - {milestone.name}
              </span>
            </S.SelectableMilestone>
          ))}
        </>
      )}

      {project && availableMilestonesFromProject.length > 0 && (
        <>
          <h3>
            {" "}
            {intl.formatMessage({
              id: "addMilestonesModal.fromProject",
            })}
          </h3>
          {availableMilestonesFromProject.map((milestone, index) => (
            <S.SelectableMilestone
              key={`available-project-milestone-${index}`}
              data-test-id={`select-${milestone.id}`}
              onClick={() =>
                toggleSelected(
                  milestone,
                  selectedMilestonesFromProject,
                  setSelectedMilestonesFromProject
                )
              }
              $selected={isCurrentlySelected(
                milestone.id,
                selectedMilestonesFromProject
              )}
              $color={token.colorPrimaryBg}
            >
              <S.SelectableMilestoneDisplay>
                <div>
                  {milestone.abbreviation} -{" "}
                  {milestone.displayName || milestone.name}
                </div>
                <div>{milestone.scheduleName}</div>
              </S.SelectableMilestoneDisplay>
            </S.SelectableMilestone>
          ))}
        </>
      )}
      {availableMilestonesFromProject?.length < 1 &&
        availableMilestonesFromTemplate?.length < 1 && (
          <h3>
            {intl.formatMessage({
              id: "addMilestonesModal.noneAvailable",
            })}
          </h3>
        )}
    </GlobalS.ResponsiveModal>
  );
};

AddMilestoneFromListModal.propTypes = {
  isOpen: bool,
  onCancel: func.isRequired,
  parentId: string,
};

export { AddMilestoneFromListModal };
